import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import "./ServiceConfiguration.css";
import { getAllServices, getSubServices } from "../../../../modules/NursignCare/ManageNurse/ManageNurseThunk";
import { getServiceItemsBySubService } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import Icons from "../../../../Assets/Icons";
import ToastPopup from "../../../../widgets/Toast/toastPopup.js";
import Widgets from "../../../../widgets";
import { getLocalizedTitle } from "../../../../utils/localizedText";

const newVisit = () => ({
  id: crypto.randomUUID(),
  serviceId: "",
  serviceName: "",
  serviceTitle: null,
  subServiceId: "",
  serviceItemId: "",
  serviceItemName: "",
  serviceItemTitle: null,
  serviceItemData: null,
  subServices: [],
  serviceItems: [],
  isMultipleDay: true,
  startDate: "",
  endDate: "",
  singleDate: "",
  startTime: "",
  endTime: "",
  frequency: "daily",
  customFrequencyMap: "",
  note: "",
});

const extractList = (payload) => {
  if (!payload) return [];
  const d = payload?.data?.data ?? payload?.data ?? payload;
  if (Array.isArray(d?.services)) return d.services;
  if (Array.isArray(d?.subServices)) return d.subServices;
  if (Array.isArray(d?.serviceItems)) return d.serviceItems;
  if (Array.isArray(d?.data)) return d.data;
  return Array.isArray(d) ? d : [];
};

const toDropdownOptions = (items, language) =>
  (items || []).map((item) => ({
    value: item._id,
    label: getLocalizedTitle(item, language),
  }));

const NoteEditor = ({ value, onChange, placeholder }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || "";
  }, []);

  const cmd = (command) => {
    ref.current?.focus();
    document.execCommand(command, false, null);
  };

  return (
    <div className="note-card">
      <div className="note-card-title">Write A Note</div>
      <div className="note-toolbar">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("bold");
          }}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("italic");
          }}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("strikethrough");
          }}
        >
          <s>S</s>
        </button>
        <div className="note-toolbar-divider" />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("justifyLeft");
          }}
        >
          &#8942;
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("justifyCenter");
          }}
        >
          &#8803;
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("justifyRight");
          }}
        >
          &#8942;
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("justifyFull");
          }}
        >
          &#8803;
        </button>
        <div className="note-toolbar-divider" />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("insertUnorderedList");
          }}
        >
          &#8226;
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cmd("insertOrderedList");
          }}
        >
          1.
        </button>
      </div>
      <div ref={ref} className="note-editor" contentEditable suppressContentEditableWarning data-placeholder={placeholder} onInput={(e) => onChange(e.currentTarget.innerHTML)} />
    </div>
  );
};

const ServiceConfiguration = ({ setPage, setEnabledSteps, initialServices, onSave }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [allServices, setAllServices] = useState([]);
  const [allSubServices, setAllSubServices] = useState([]);
  const [visits, setVisits] = useState([newVisit()]);

  useEffect(() => {
    dispatch(getAllServices({ page: 1, limit: 100, isActive: true })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        const apiList = extractList(result.payload);
        setAllServices((prev) => {
          const apiIds = new Set(apiList.map((s) => s._id));
          const preserved = prev.filter((s) => !apiIds.has(s._id));
          return [...apiList, ...preserved];
        });
      }
    });

    dispatch(getSubServices({ limit: 20 })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        setAllSubServices(extractList(result.payload));
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (!initialServices?.length) return;

    const preVisits = initialServices.map((svc) => ({
      ...newVisit(),
      serviceId: svc.service?._id || "",
      serviceName: getLocalizedTitle(svc.service),
      serviceTitle: svc.service?.title || null,
      subServiceId: svc.subService?._id || "",
      serviceItemId: svc.serviceItem?._id || "",
      serviceItemName: getLocalizedTitle(svc.serviceItem),
      serviceItemTitle: svc.serviceItem?.title || null,
      serviceItemData: svc.serviceItem || null,
      subServices: svc.subService?._id ? [{ _id: svc.subService._id, title: svc.subService.title }] : [],
      serviceItems: svc.serviceItem?._id ? [{ _id: svc.serviceItem._id, title: svc.serviceItem.title }] : [],
      startTime: svc.startTime || "",
      endTime: svc.endTime || "",
      startDate: svc.startDate || "",
      endDate: svc.endDate || "",
      singleDate: svc.singleDate || svc.date || "",
      isMultipleDay: svc.isMultipleDay ?? !!(svc.startDate && svc.endDate && svc.startDate !== svc.endDate),
    }));

    setVisits(preVisits);

    const serviceStubs = initialServices
      .filter((svc) => svc.service?._id)
      .map((svc) => ({
        _id: svc.service._id,
        title: {
          en: svc.service.title?.en || svc.service.title?.vi || svc.service.title,
          vi: svc.service.title?.vi || svc.service.title?.en || svc.service.title,
        },
      }));

    if (serviceStubs.length) {
      setAllServices((prev) => {
        const existingIds = new Set(prev.map((s) => s._id));
        const newStubs = serviceStubs.filter((s) => !existingIds.has(s._id));
        return [...prev, ...newStubs];
      });
    }

    const subServiceStubs = initialServices.filter((svc) => svc.subService?._id).map((svc) => ({ _id: svc.subService._id, title: svc.subService.title }));

    if (subServiceStubs.length) {
      setAllSubServices((prev) => {
        const existingIds = new Set(prev.map((s) => s._id));
        const newStubs = subServiceStubs.filter((s) => !existingIds.has(s._id));
        return [...prev, ...newStubs];
      });
    }

    preVisits.forEach((v, idx) => {
      if (v.serviceId) {
        dispatch(getSubServices(v.serviceId)).then((result) => {
          if (result.meta.requestStatus === "fulfilled") {
            const data = extractList(result.payload);
            if (data.length) {
              setVisits((prev) => prev.map((pv, i) => (i === idx ? { ...pv, subServices: data } : pv)));
            }
          }
        });
      }
      if (v.subServiceId) {
        dispatch(getServiceItemsBySubService(v.subServiceId)).then((result) => {
          if (result.meta.requestStatus === "fulfilled") {
            const data = extractList(result.payload);
            if (data.length) {
              setVisits((prev) => prev.map((pv, i) => (i === idx ? { ...pv, serviceItems: data } : pv)));
            }
          }
        });
      }
    });
  }, [initialServices, dispatch]);

  const updateVisit = (id, updates) => {
    setVisits((prev) => prev.map((v) => (v.id === id ? { ...v, ...updates } : v)));
  };

  const handleServiceChange = (visitId, serviceId) => {
    const service = allServices.find((s) => s._id === serviceId);
    updateVisit(visitId, {
      serviceId,
      serviceName: getLocalizedTitle(service, i18n.language),
      serviceTitle: service?.title || null,
      subServiceId: "",
      serviceItemId: "",
      serviceItemName: "",
      serviceItemTitle: null,
      serviceItemData: null,
      subServices: [],
      serviceItems: [],
    });
    if (!allSubServices.length) {
      dispatch(getSubServices({ limit: 20 })).then((result) => {
        if (result.meta.requestStatus === "fulfilled") setAllSubServices(extractList(result.payload));
      });
    }
  };

  const handleSubServiceChange = (visitId, subServiceId) => {
    updateVisit(visitId, {
      subServiceId,
      serviceItemId: "",
      serviceItemName: "",
      serviceItemData: null,
      serviceItems: [],
    });
    if (subServiceId) {
      dispatch(getServiceItemsBySubService(subServiceId)).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          updateVisit(visitId, { serviceItems: extractList(result.payload) });
        }
      });
    }
  };

  const handleServiceItemChange = (visitId, serviceItemId, visit) => {
    const item = visit.serviceItems.find((s) => s._id === serviceItemId);
    updateVisit(visitId, {
      serviceItemId,
      serviceItemName: getLocalizedTitle(item, i18n.language),
      serviceItemTitle: item?.title || null,
      serviceItemData: item || null,
    });
  };

  const handleNext = () => {
    for (const v of visits) {
      if (!v.serviceId || !v.subServiceId || !v.serviceItemId) {
        ToastPopup.error(t("fillAllFields"));
        return;
      }
    }

    const serviceData = visits.map((v) => ({
      service: v.serviceId,
      subService: v.subServiceId,
      serviceItem: v.serviceItemId,
    }));

    onSave?.({ services: serviceData, visits });
    setEnabledSteps?.((prev) => [...new Set([...prev, 3])]);
    setPage?.(3);
  };

  const requestedChips = visits
    .map(
      (v) =>
        getLocalizedTitle(
          allServices.find((service) => service._id === v.serviceId),
          i18n.language,
        ) || v.serviceName,
    )
    .filter(Boolean);

  return (
    <div className="service-configuration-container">
      {requestedChips.length > 0 && (
        <div className="whp-container requested-services-container">
          <div className="title-hr2">
            <p>{t("requestedServices")}</p>
            <hr />
          </div>
          <div className="requested-services-list">
            {requestedChips.map((name, i) => (
              <div key={i} className="requested-service-item circular">
                <div className="service-circle selected">
                  <img src={Icons.tick} alt="✓" />
                </div>
                <div className="service-badge">{name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {visits.map((visit, idx) => (
        <div key={visit.id} className="whp-container service-block">
          <div className="form-section-header">
            <div className="title-hr2">
              <p>{t("visit") || "Visit"}</p>
              <hr />
            </div>
          </div>
          <div className="cascade-dropdowns" style={{ flexWrap: "wrap" }}>
            {(() => {
              const serviceOptions = toDropdownOptions(allServices, i18n.language);
              const subServiceOptions = toDropdownOptions(allSubServices.length ? allSubServices : visit.subServices, i18n.language);
              const serviceItemOptions = toDropdownOptions(visit.serviceItems, i18n.language);

              return (
                <>
                  <div className="service-input-box form-section-col">
                    <span>{t("serviceGroup") || "Service Group"} *</span>
                    <Widgets.NewSelectDropdown
                      options={serviceOptions}
                      selectedValue={serviceOptions.find((option) => option.value === visit.serviceId) || null}
                      onChange={(option) => handleServiceChange(visit.id, option?.value || "")}
                      placeholder={t("selectService") || "Select Service"}
                      big
                      noMargin
                    />
                  </div>
                  <div className="service-input-box form-section-col">
                    <span>{t("subServiceGroup") || "Sub-Service Group"} *</span>
                    <Widgets.NewSelectDropdown
                      options={subServiceOptions}
                      selectedValue={subServiceOptions.find((option) => option.value === visit.subServiceId) || null}
                      onChange={(option) => handleSubServiceChange(visit.id, option?.value || "")}
                      placeholder={t("selectSubService") || "Select Sub-Service"}
                      big
                      noMargin
                    />
                  </div>
                  <div className="service-input-box form-section-col">
                    <span>{t("serviceItem") || "Service Item"} *</span>
                    <Widgets.NewSelectDropdown
                      options={serviceItemOptions}
                      selectedValue={serviceItemOptions.find((option) => option.value === visit.serviceItemId) || null}
                      onChange={(option) => handleServiceItemChange(visit.id, option?.value || "", visit)}
                      placeholder={t("selectServiceItem") || "Select Service Item"}
                      big
                      noMargin
                    />
                  </div>
                </>
              );
            })()}
          </div>

          {/* Write A Note section commented out
          <div className="note-section-wrapper">
            <NoteEditor value={visit.note} onChange={(html) => updateVisit(visit.id, { note: html })} placeholder={t("description")} />
          </div>
          */}
        </div>
      ))}

      <div className="form-section-footer">
        <button className="form-section-cancel" onClick={() => setPage?.(1)}>
          {t("back")}
        </button>
        <button className="form-section-create" onClick={handleNext}>
          {t("next")}
        </button>
      </div>
    </div>
  );
};

export default ServiceConfiguration;
